/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { FC, useState } from "react";
import { Files } from "interfaces";
import { 
  Typography, 
  Box, 
  Card,
  Alert,
  styled,
  IconButton,
  Tooltip,
  CircularProgress,
  Button as MuiButton
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MinimizeIcon from "@mui/icons-material/Minimize";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import Button from "components/generals/Button";
import { useTranslation } from "react-i18next";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import { canUseDocViewer, getFileCategory, isOfficeDocument, isViewableInline } from "utils/blobProxy.utils";
import { useAuthenticatedAsset } from "hooks/useAuthenticatedAsset.hook";
import { OfficeDocumentRenderer } from "./OfficeDocumentRenderer";
import { RoutesConstants } from "medicalsystem-shared";

const ViewerContainer = styled(Box)(({ theme }) => ({
  '& .react-doc-viewer': {
    height: '100%',
    '& #react-doc-viewer': {
      height: '900px !important',
    }
  },
  '& .pdf-viewer': {
    width: '100%',
    height: '900px',
    border: 'none',
    backgroundColor: '#ffffff',
  },
  '& .image-viewer': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '900px',
    backgroundColor: '#f5f5f5',
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    }
  },
  '& .video-viewer': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '900px',
    backgroundColor: '#000',
    '& video': {
      maxWidth: '100%',
      maxHeight: '100%',
    }
  },
  '& .audio-viewer': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '900px',
    backgroundColor: '#f5f5f5',
    '& audio': {
      width: '100%',
      maxWidth: '500px',
    }
  }
}));

const FilesViewer: FC<Files> = ({ asset }) => {
  const { t } = useTranslation();
  const [showViewer, setShowViewer] = useState(true);
  const [viewerError, setViewerError] = useState<string | null>(null);
  
  // Fetch authenticated asset (handles JWT auth for proxy mode)
  const { blobUrl, isLoading: assetLoading, error: assetError, progress } = useAuthenticatedAsset(asset);

  const handleToggleViewer = () => {
    setShowViewer(!showViewer);
    setViewerError(null);
  };

  const handleOpenInNewTab = () => {
    // Build the asset viewer URL
    // Build the asset viewer URL robustly by replacing the route param
    const viewerUrl = RoutesConstants.ASSET_VIEWER.replace(':assetId', encodeURIComponent(asset.id));
    
    // Use localStorage instead of sessionStorage because sessionStorage is isolated per tab
    // and noopener prevents the new window from accessing the parent's sessionStorage
    const stateKey = `asset-viewer-${
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`
    }`;
    const dataToStore = JSON.stringify({ asset });
    localStorage.setItem(stateKey, dataToStore);
    const fullUrl = `${window.location.origin}${viewerUrl}?stateKey=${stateKey}`;
    window.open(fullUrl, '_blank', 'noreferrer');
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('pdf')) return <PictureAsPdfIcon />;
    if (mimeType.includes('image')) return <ImageIcon />;
    if (mimeType.includes('video')) return <VideoLibraryIcon />;
    if (mimeType.includes('audio')) return <AudioFileIcon />;
    if (mimeType.includes('doc') || mimeType.includes('text')) return <DescriptionIcon />;
    return <InsertDriveFileIcon />;
  };

  const renderOfficeViewer = () => {
    // Use blobUrl from useAuthenticatedAsset hook
    // The hook handles authentication and proxy automatically
    return (
      <OfficeDocumentRenderer
        originalUrl={blobUrl}
        filename={asset.orgName}
        mimetype={asset.mimetype}
      />
    );
  };

  const renderContent = () => {
    const category = getFileCategory(asset.mimetype);
    
    // Show loading state while fetching authenticated asset
    if (assetLoading) {
      return (
        <Box className="loading-container">
          <Box className="progress-wrapper">
            <CircularProgress 
              variant="determinate" 
              value={progress}
              size={80}
              thickness={4}
              sx={{ color: 'primary.main' }}
            />
            <Box className="progress-percentage">
              <Typography 
                variant="h6" 
                component="div" 
                color="text.secondary"
              >
                {`${progress}%`}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" className="loading-message">
            {t("documentViewer.loading")}
          </Typography>
        </Box>
      );
    }
    
    // Show error if asset failed to load
    if (assetError || !blobUrl) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {assetError || t("documentViewer.errorLoadingFile")}
        </Alert>
      );
    }
    
    try {
      if (isOfficeDocument(asset.mimetype)) {
        return renderOfficeViewer();
      }
      if (category === 'pdf') {
        return (
          <iframe
            src={`${blobUrl}#toolbar=1&navpanes=1&scrollbar=1`}
            className="pdf-viewer"
            title={asset.orgName}
            onError={() => setViewerError(t("documentViewer.errorLoadingFile"))}
          />
        );
      }
      if (canUseDocViewer(asset.mimetype)) {
        return (
          <DocViewer
            documents={[{ uri: blobUrl }]}
            pluginRenderers={DocViewerRenderers}
            className="viewer-height"
            config={{
              header: {
                disableHeader: false,
                disableFileName: false,
                retainURLParams: false
              },
            }}
          />
        );
      }

      switch (category) {
        case 'image': {
          return (
            <Box className="image-viewer">
              <img 
                key={blobUrl} // Force re-render when blobUrl changes
                src={blobUrl}
                alt={asset.orgName}
                onError={() => setViewerError(t("documentViewer.errorLoadingFile"))}
              />
            </Box>
          );
        }

        case 'video':
          return (
            <Box className="video-viewer">
              <video 
                controls 
                src={blobUrl}
                onError={() => setViewerError(t("documentViewer.errorLoadingFile"))}
              >
                {t("documentViewer.videoNotSupported")}
              </video>
            </Box>
          );

        case 'audio':
          return (
            <Box className="audio-viewer">
              <audio 
                controls 
                src={blobUrl}
                onError={() => setViewerError(t("documentViewer.errorLoadingFile"))}
              >
                {t("documentViewer.audioNotSupported")}
              </audio>
            </Box>
          );

        case 'text':
          return (
            <iframe
              src={blobUrl}
              className="fallback-viewer"
              title={asset.orgName}
              onError={() => setViewerError(t("documentViewer.errorLoadingFile"))}
            />
          );

        default:
          return (
            <iframe
              src={blobUrl}
              className="fallback-viewer"
              title={asset.orgName}
              onError={() => setViewerError(t("documentViewer.errorLoadingFile"))}
            />
          );
      }
    } catch (error) {
      console.error("Viewer error:", error);
      setViewerError(t("documentViewer.errorLoadingFile"));
      return (
        <Box className="error-container">
          <Typography variant="h6" className="error-message">
            {t("documentViewer.errorLoadingFile")}
          </Typography>
          <Button 
            variant="outlined" 
            className="error-button"
            onClick={() => window.open(blobUrl, '_blank', 'noopener,noreferrer')}
            startIcon={<OpenInNewIcon />}
          >
            {t("documentViewer.openExternal")}
          </Button>
        </Box>
      );
    }
  };

  return (
    <Card className="card">
      <Box className="header">
        <Box className="header-left">
          <Box className="icon">
            {getFileIcon(asset.mimetype)}
          </Box>
          <Box className="file-info">
            <Typography className="file-name">
              {asset.orgName}
            </Typography>
            <Typography className="file-type">
              {t("documentViewer.fileType")}: {asset.mimetype}
            </Typography>
          </Box>
        </Box>

        <Box className="header-right">
          {isViewableInline(asset.mimetype) && (
            <Button
              color={showViewer ? "secondary" : "primary"}
              onClick={handleToggleViewer}
              startIcon={showViewer ? <MinimizeIcon /> : <ExpandMoreIcon />}
            >
              {showViewer ? t("documentViewer.hidePreview") : t("documentViewer.showPreview")}
            </Button>
          )}

          <Tooltip title={t("documentViewer.openInNewTab")}>
            <IconButton
              color="info"
              onClick={handleOpenInNewTab}
              aria-label={t("documentViewer.openInNewTab")}
            >
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {showViewer && isViewableInline(asset.mimetype) && (
        <Box className="viewer-container">
          {viewerError && (
            <Alert severity="error" className="error-alert">
              {viewerError}
              <Button 
                size="small" 
                onClick={() => {
                  setViewerError(null);
                  window.open(blobUrl || asset.url, '_blank', 'noopener,noreferrer');
                }}
              >
                {t("documentViewer.openExternal")}
              </Button>
            </Alert>
          )}

          <Box className="viewer-box">
            <ViewerContainer key={asset.id || blobUrl}>
              {renderContent()}
            </ViewerContainer>
          </Box>
        </Box>
      )}

      {!isViewableInline(asset.mimetype) && (
        <Box className="no-preview">
          <Typography className="no-preview-title">
            {t("documentViewer.previewNotAvailable")}
          </Typography>
          <Typography className="no-preview-message">
            {t("documentViewer.useOpenExternal")}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default FilesViewer;